import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import type { Currency, FinanceTagBreakdownItem } from '@etiquetachef/types';
import { formatCurrencyCents } from '@/features/dashboard-finance/formatters';
import { useTagLabel } from './financeAnalysisFormat';

/**
 * Componentes compartilhados das telas de Análise do Financeiro interno (Visão geral, Caixa,
 * Balanço): valor com sinal e a tabela "por tag".
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro PR D). Decisão #72 = tags livres; a quebra
 * por tag é central nas três telas. Helpers puros e o hook `useTagLabel` vivem em
 * `financeAnalysisFormat.ts`.
 *
 * Usado por: `routes/FinanceOverviewPage.tsx`, `routes/FinanceCashflowPage.tsx`,
 * `routes/FinanceBalancePage.tsx` (web `apps/admin`).
 *
 * Dados: `FinanceTagBreakdownItem` (`packages/types/src/finance.ts`) — `totalsByCurrency[ccy]`
 * com `inCents`/`outCents`/`netCents` em CENTAVOS, formatados por `formatCurrencyCents`.
 */

/**
 * Valor em cents colorido por sinal (verde ≥0, vermelho <0).
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro PR D).
 * Usado por: `TagBreakdownTable` (aqui), `routes/FinanceOverviewPage.tsx`,
 * `FinanceCashflowPage.tsx`, `FinanceBalancePage.tsx`.
 */
export function SignedCents({
  cents,
  currency,
}: {
  cents: number;
  currency: Currency;
}): React.JSX.Element {
  return (
    <Box textAlign="right" color={cents < 0 ? 'text-status-error' : 'text-status-success'}>
      {formatCurrencyCents(cents, currency)}
    </Box>
  );
}

type FlatRow = {
  key: string;
  tag: string;
  currency: Currency;
  inCents: number;
  outCents: number;
  netCents: number;
};

/** Uma linha por (tag, moeda), com chave `tag|moeda` para o `trackBy`. */
function flatten(items: FinanceTagBreakdownItem[]): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const item of items) {
    for (const [currency, totals] of Object.entries(item.totalsByCurrency)) {
      rows.push({
        key: `${item.tag}|${currency}`,
        tag: item.tag,
        currency: currency as Currency,
        inCents: totals.inCents,
        outCents: totals.outCents,
        netCents: totals.netCents,
      });
    }
  }
  return rows;
}

/**
 * Tabela "por tag" reusada nas três telas. Um lançamento multi-tag aparece em
 * cada tag (dupla contagem intencional) — a soma das linhas ≠ total geral.
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro PR D).
 * Usado por: `routes/FinanceOverviewPage.tsx` (`/finance`), `FinanceCashflowPage.tsx`
 * (`/finance/cashflow`), `FinanceBalancePage.tsx` (`/finance/balance`) — menu Financeiro.
 */
export function TagBreakdownTable({
  items,
  title,
}: {
  items: FinanceTagBreakdownItem[];
  title: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const tagLabel = useTagLabel();
  const rows = flatten(items);

  return (
    <Table<FlatRow>
      items={rows}
      trackBy="key"
      variant="container"
      header={<Header variant="h3">{title}</Header>}
      empty={
        <Box textAlign="center" color="text-status-inactive" padding="m">
          {t('admin.finance.analysis.empty')}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'tag',
          header: t('admin.finance.analysis.byTag.tag'),
          cell: (r): React.ReactNode =>
            r.tag === '' ? <Box color="text-status-inactive">{tagLabel(r.tag)}</Box> : tagLabel(r.tag),
        },
        {
          id: 'currency',
          header: t('admin.finance.analysis.columns.currency'),
          cell: (r): React.ReactNode => r.currency,
        },
        {
          id: 'in',
          header: t('admin.finance.analysis.columns.in'),
          cell: (r): React.ReactNode => (
            <Box textAlign="right">{formatCurrencyCents(r.inCents, r.currency)}</Box>
          ),
        },
        {
          id: 'out',
          header: t('admin.finance.analysis.columns.out'),
          cell: (r): React.ReactNode => (
            <Box textAlign="right">{formatCurrencyCents(r.outCents, r.currency)}</Box>
          ),
        },
        {
          id: 'net',
          header: t('admin.finance.analysis.columns.net'),
          cell: (r): React.ReactNode => <SignedCents cents={r.netCents} currency={r.currency} />,
        },
      ]}
    />
  );
}
